import createApiClient from './apiClient';
import { cachedRequest, invalidateStudentCache, invalidateCache } from '../utils/apiDataCache';

const studentApi = createApiClient(process.env.REACT_APP_ATHENIAN_STUDENT_SERVICE_URL);

export const getCompliance = (studentId, complianceId, etag = null, { forceFresh = false } = {}) => {
  const params = complianceId ? `?compliance_id=${complianceId}` : '';
  const cacheKey = `student:${studentId}:compliance:${complianceId || 'latest'}:${etag || 'full'}`;
  if (etag) {
    const headers = { 'If-None-Match': etag.startsWith('"') ? etag : `"${etag}"` };
    return studentApi.get(`/${studentId}/compliance${params}`, {
      headers,
      validateStatus: (status) => status === 200 || status === 304 || status === 404,
    });
  }
  if (forceFresh) invalidateCache(cacheKey);
  return cachedRequest(cacheKey, () =>
    studentApi.get(`/${studentId}/compliance${params}`, {
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      validateStatus: (status) => status === 200 || status === 304 || status === 404,
    }),
  );
};

export const getComplianceStatus = (studentId, complianceId) => {
  const params = complianceId ? `?compliance_id=${complianceId}` : '';
  return studentApi.get(`/${studentId}/compliance/status${params}`);
};

export const getActiveJobs = (studentId) =>
  studentApi.get(`/${studentId}/jobs/active`);

export const listStudentJobs = (studentId, limit = 40) =>
  studentApi.get(`/${studentId}/jobs`, { params: { limit } });

export const createJobStreamTicket = (studentId) =>
  studentApi.post(`/${studentId}/jobs/stream-ticket`);

export const getJobStatus = (studentId, jobId) =>
  studentApi.get(`/${studentId}/jobs/${encodeURIComponent(jobId)}`);

export const getJobLogs = (studentId, jobId, filename = 'full-job.log') =>
  studentApi.get(
    `/${studentId}/jobs/${encodeURIComponent(jobId)}/logs`,
    { params: { filename }, responseType: 'text' },
  );

export const cancelJob = (studentId, jobId) => {
  invalidateStudentCache(studentId);
  return studentApi.post(`/${studentId}/jobs/${encodeURIComponent(jobId)}/cancel`);
};

export const retryJob = (studentId, jobId) => {
  invalidateStudentCache(studentId);
  return studentApi.post(`/${studentId}/jobs/${encodeURIComponent(jobId)}/retry`);
};

export const getComplianceCycles = (studentId, { forceFresh = false } = {}) => {
  const cacheKey = `student:${studentId}:compliance-cycles`;
  if (forceFresh) invalidateCache(cacheKey);
  return cachedRequest(cacheKey, () =>
    studentApi.get(`/${studentId}/compliance/list?page=1&limit=50`));
};

export const updateSection = (studentId, sectionName, data, complianceId) => {
  invalidateStudentCache(studentId);
  const params = complianceId ? `?compliance_id=${complianceId}` : '';
  return studentApi.put(`/${studentId}/compliance/sections/${sectionName}${params}`, data);
};

export const rerunCompliance = (studentId, complianceId) => {
  invalidateStudentCache(studentId);
  return studentApi.post(`/${studentId}/compliance/rerun`, complianceId ? { compliance_id: complianceId } : {});
};

export const rerunSection = (studentId, sectionName, data = null, complianceId) => {
  invalidateStudentCache(studentId);
  const body = { ...(data || {}) };
  if (complianceId) body.compliance_id = complianceId;
  return studentApi.post(`/${studentId}/compliance/rerun/${sectionName}`, Object.keys(body).length ? body : null);
};

export const getComplianceActivity = (studentId, params) =>
  cachedRequest(
    `student:${studentId}:activity:${params?.page || 1}:${params?.limit || 20}`,
    () => studentApi.get(`/${studentId}/compliance/activity`, { params }),
    45_000,
  );

export const getExportPdfUrl = (studentId) => {
  const base = process.env.REACT_APP_ATHENIAN_STUDENT_SERVICE_URL || '';
  return `${base}/${studentId}/compliance/export-pdf`;
};

export const exportPdf = (studentId, complianceId) => {
  const params = complianceId ? `?compliance_id=${complianceId}` : '';
  return studentApi.get(`/${studentId}/compliance/export-pdf${params}`, { responseType: 'blob' });
};

export const exportCsv = (studentId, complianceId) => {
  const params = complianceId ? `?compliance_id=${complianceId}` : '';
  return studentApi.get(`/${studentId}/compliance/export-csv${params}`, { responseType: 'blob' });
};

export const deleteComplianceCycle = (studentId, complianceId) => {
  invalidateStudentCache(studentId);
  return studentApi.delete(`/${studentId}/compliance/${complianceId}`);
};

export const assignFileToCycle = (studentId, fileId, complianceId) => {
  invalidateStudentCache(studentId);
  return studentApi.post(`/${studentId}/compliance/assign-file`, { file_id: fileId, compliance_id: complianceId });
};

export const stopAll = (studentId) => {
  invalidateStudentCache(studentId);
  return studentApi.post(`/${studentId}/compliance/stop`);
};

export const stopRerun = (studentId) => {
  invalidateStudentCache(studentId);
  return studentApi.post(`/${studentId}/compliance/stop/rerun`);
};

export const stopSectionRerun = (studentId, sectionName) => {
  invalidateStudentCache(studentId);
  return studentApi.post(`/${studentId}/compliance/stop/rerun/${sectionName}`);
};
